import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '@/components/DataTable';
import { api, errorMessage } from '@/lib/api';
import { Badge, Button, Input, PageHeader, Select, Toast } from '@/components/ui';
import { formatDateTime, formatPhone } from '@/lib/format';

interface Transfer {
  id: string;
  call_id: string;
  attempt: number;
  kind: string;
  destination: string;
  requested_at: string;
  connected_at: string | null;
  wait_sec: number | null;
  human_agent: string | null;
  result: string;
  failure_reason: string | null;
  fallback_action: string | null;
  caller_number: string | null;
  vicidial_campaign: string | null;
  bot_name: string | null;
  /** What the human made of the lead. Null until somebody says. */
  sale_outcome: string | null;
  sale_value: number | null;
}

/**
 * What a person can say came of a transfer.
 *
 * Deliberately short. A list long enough to describe every situation is one
 * nobody fills in, and an empty column is worth nothing.
 *
 * Kept SEPARATE from `result`, which is whether the transfer technically
 * connected. "Sale made" is not an answer to "did the bridge work", and one
 * column holding both would destroy the only record of why a transfer failed.
 */
const SALE_OUTCOMES: Array<{ value: string; label: string }> = [
  { value: '', label: 'Not set' },
  { value: 'SALE', label: 'Sale made' },
  { value: 'PENDING', label: 'Working it' },
  { value: 'CALLBACK', label: 'Call back' },
  { value: 'NO_SALE', label: 'No sale' },
  { value: 'NOT_QUALIFIED', label: 'Not qualified' },
];


const RESULT_TONES: Record<string, 'green' | 'amber' | 'red' | 'gray'> = {
  CONNECTED: 'green',
  PENDING: 'amber',
  NO_AGENT: 'red',
  TIMEOUT: 'red',
  FAILED: 'red',
  ABANDONED: 'gray',
  CANCELLED: 'gray',
};

export default function Transfers() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [total, setTotal] = useState<number | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(
    null,
  );

  // Stable, so `DataTable`'s effect does not re-fire on every render.
  const onTotal = useCallback((value: number) => setTotal(value), []);
  const [query, setQuery] = useState<Record<string, string | number | boolean | null | undefined>>({});
  const onQuery = useCallback((q: Record<string, string | number | boolean | null | undefined>) => setQuery(q), []);

  // What a sale is usually worth here, so twenty transfers on a Friday
  // afternoon do not mean typing the same number twenty times.
  const settings = useQuery({
    queryKey: ['client-settings'],
    queryFn: () => api.get<{ default_sale_value: number | null }>('/settings'),
  });
  const defaultValue = settings.data?.default_sale_value ?? null;

  const setOutcome = useMutation({
    mutationFn: ({
      id,
      outcome,
      value,
    }: {
      id: string;
      outcome: string;
      value?: number | null;
    }) =>
      api.put(`/transfers/${id}/outcome`, {
        outcome: outcome || null,
        ...(value === undefined ? {} : { value }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['transfers'] });
      setToast({ message: 'Outcome saved.', tone: 'good' });
    },
    onError: (err) =>
      setToast({ message: errorMessage(err, 'Could not save it'), tone: 'bad' }),
  });

  return (
    <div className="w-full">
      <PageHeader
        title="Transfers"
        description="Every handover to a human verifier, including the ones that failed."
        actions={
          // The count, in the header. It existed only in the pager, so a page
          // holding every transfer it had showed no number at all -- which is
          // exactly when somebody is asking how many.
          total === null ? null : (
            <div className="flex items-center gap-3">
              <span className="text-sm text-ink-500">
                <strong className="text-ink-900 dark:text-ink-50">
                  {total.toLocaleString()}
                </strong>{' '}
                {total === 1 ? 'transfer' : 'transfers'}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => api.download('/reports/transfers.csv', query)}
              >
                Export CSV
              </Button>
            </div>
          )
        }
      />

      <DataTable<Transfer>
        queryKey="transfers"
        path="/transfers"
        searchable={false}
        onTotal={onTotal}
        onQuery={onQuery}
        onRowClick={(transfer) => navigate(`/calls/${transfer.call_id}`)}
        emptyTitle="No transfers yet"
        emptyDescription="Transfers appear here once a bot qualifies a lead and hands it to an agent."
        filters={[
          {
            key: 'result',
            label: 'Result',
            options: Object.keys(RESULT_TONES).map((value) => ({
              value,
              label: value.replace(/_/g, ' '),
            })),
          },
        ]}
        columns={[
          {
            key: 'requested_at',
            header: 'Requested',
            render: (t) => (
              <span className="whitespace-nowrap text-xs text-ink-500">
                {formatDateTime(t.requested_at)}
              </span>
            ),
          },
          {
            key: 'caller',
            header: 'Customer',
            render: (t) => (
              <span className="font-mono text-sm">{formatPhone(t.caller_number)}</span>
            ),
          },
          {
            key: 'destination',
            header: 'Destination',
            render: (t) => (
              <div>
                <div className="text-sm">{t.destination}</div>
                <div className="text-xs text-ink-500">{t.kind?.replace(/_/g, ' ')}</div>
              </div>
            ),
          },
          {
            key: 'sale_outcome',
            header: 'Outcome',
            render: (t) => (
              // Stops the row-click through to the call: somebody changing a
              // dropdown is not asking to navigate away from it.
              <div onClick={(event) => event.stopPropagation()}>
                <Select
                  value={t.sale_outcome ?? ''}
                  disabled={setOutcome.isPending}
                  onChange={(event) =>
                    setOutcome.mutate({
                      id: t.id,
                      outcome: event.target.value,
                      // A new sale starts at the account's usual figure,
                      // which is the right answer most of the time and
                      // editable when it is not.
                      value:
                        event.target.value === 'SALE'
                          ? (t.sale_value ?? defaultValue)
                          : null,
                    })
                  }
                  className="w-36 text-xs"
                >
                  {SALE_OUTCOMES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </div>
            ),
          },
          {
            key: 'sale_value',
            header: 'Worth',
            numeric: true,
            render: (t) =>
              t.sale_outcome !== 'SALE' ? (
                <span className="text-ink-300">—</span>
              ) : (
                <div onClick={(event) => event.stopPropagation()}>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    defaultValue={t.sale_value ?? defaultValue ?? ''}
                    placeholder={defaultValue ? String(defaultValue) : '0.00'}
                    className="w-24 text-right text-xs"
                    // On blur, not per keystroke: a request for every digit
                    // of "1250" writes 1, then 12, then 125.
                    onBlur={(event) => {
                      const next = event.target.value.trim();
                      const parsed = next === '' ? null : Number(next);
                      if (parsed === (t.sale_value ?? null)) return;
                      setOutcome.mutate({
                        id: t.id,
                        outcome: 'SALE',
                        value: parsed,
                      });
                    }}
                  />
                </div>
              ),
          },
          {
            key: 'agent',
            header: 'Agent',
            render: (t) => t.human_agent ?? <span className="text-ink-400">—</span>,
          },
          {
            key: 'wait',
            header: 'Wait',
            numeric: true,
            render: (t) => (t.wait_sec === null ? '—' : `${t.wait_sec}s`),
          },
          { key: 'attempt', header: 'Attempt', numeric: true, render: (t) => t.attempt },
          {
            key: 'result',
            header: 'Result',
            render: (t) => (
              <div>
                <Badge tone={RESULT_TONES[t.result] ?? 'gray'}>
                  {t.result.replace(/_/g, ' ')}
                </Badge>
                {t.fallback_action && (
                  <div className="mt-0.5 text-xs text-ink-500">
                    → {t.fallback_action.replace(/_/g, ' ').toLowerCase()}
                  </div>
                )}
                {t.failure_reason && (
                  <div className="mt-0.5 text-xs text-red-600">{t.failure_reason}</div>
                )}
              </div>
            ),
          },
        ]}
      />
      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}
